/**
 * The public online booking page (/book/:slug, no sign-in): location -> service -> staff -> day ->
 * time -> details -> confirmation. Steps with a single choice are skipped. Times come from the
 * API's availability endpoint (the same rules the receptionist books by), in the location's time
 * zone; the page uses the business's product palette.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarCheck, Clock, MapPin } from 'lucide-react';
import { Alert, Button, Card, CardContent, Checkbox, ErrorState, Input, Label, LoadingState, Spinner, formatMoney } from '@buzzbee/ui';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, errorMessage, get, post } from '../lib/api';
import { useBrand, type Product } from '../lib/session';

type Hours = Partial<Record<string, { opens: string; closes: string }[]>>;
type PublicService = { name: string; durationMinutes: number; priceCents: number | null; depositCents: number | null };
type PublicLocation = { name: string; address: string | null; timezone: string; hours: Hours; requestMode: boolean; offersRebooking?: boolean; leadTimeMinutes: number; services: PublicService[]; staff: { name: string; services: string[] }[] };
type BookingInfo = { business: { name: string; product: Product; productName: string }; poweredBy: string | null; turnstileSiteKey?: string | null; locations: PublicLocation[] };
/** What the booking executor returned (ids removed); extra fields such as a deposit link pass through. */
type BookingResult = Record<string, unknown> & { status?: string; needs_confirmation?: boolean; service?: string; staff?: string; starts_at?: string; location?: string };

type Step = 'location' | 'service' | 'staff' | 'day' | 'time' | 'details' | 'done';
const ANYONE = '';
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** The next `count` days at the location (YYYY-MM-DD), with whether it opens that day. */
function upcomingDays(timezone: string, hours: Hours, count = 14): { date: string; open: boolean; label: string; weekday: string }[] {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  return Array.from({ length: count }, (_, i) => {
    // Calendar arithmetic at UTC noon: no time zone or DST edge can move it to another day.
    const day = new Date(Date.UTC(y, m - 1, d + i, 12));
    const date = day.toISOString().slice(0, 10);
    return {
      date,
      open: (hours[WEEKDAYS[day.getUTCDay()]!] ?? []).length > 0,
      weekday: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', weekday: 'short' }).format(day),
      label: new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(day),
    };
  });
}

/** "2026-06-09T13:30:00-04:00" -> "1:30 PM" (the ISO time is already the location's local time). */
function clockLabel(iso: string): string {
  const [h, min] = iso.slice(11, 16).split(':').map(Number) as [number, number];
  return `${h % 12 || 12}:${String(min).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function dayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d, 12)));
}

function Choice({ onClick, selected, children, disabled }: { onClick: () => void; selected?: boolean; children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`w-full rounded-xl border p-4 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-40 ${selected ? 'border-primary bg-primary/[0.06]' : 'border-border bg-card'}`}
    >
      {children}
    </button>
  );
}

export function BookingPage({ slug }: { slug: string }) {
  const info = useQuery({ queryKey: ['booking-page', slug], queryFn: () => get<BookingInfo>(`/api/public/book/${encodeURIComponent(slug)}`), retry: false });
  useBrand(info.data?.business.product ?? null);
  useEffect(() => {
    if (info.data) document.title = `Book with ${info.data.business.name}`;
  }, [info.data]);

  return (
    <div className="min-h-dvh bg-background">
      <main className="mx-auto max-w-lg px-4 pb-16 pt-8">
        {info.isLoading ? <LoadingState className="min-h-[50dvh]" /> : null}
        {info.error ? (
          <ErrorState
            title={info.error instanceof ApiError && info.error.status === 404 ? 'Online booking is not available' : 'Could not load this page'}
            error={info.error instanceof ApiError && info.error.status === 404 ? 'This business does not take bookings online right now. Please call them.' : info.error}
            onRetry={info.error instanceof ApiError && info.error.status === 404 ? undefined : () => info.refetch()}
          />
        ) : null}
        {info.data ? <Flow slug={slug} info={info.data} /> : null}
        {info.data?.poweredBy ? <p className="mt-10 text-center text-xs text-muted-foreground">Powered by {info.data.poweredBy}</p> : null}
      </main>
    </div>
  );
}

function Flow({ slug, info }: { slug: string; info: BookingInfo }) {
  const [locationName, setLocationName] = useState<string | null>(info.locations.length === 1 ? info.locations[0]!.name : null);
  const [serviceName, setServiceName] = useState<string | null>(null);
  const [staffName, setStaffName] = useState<string>(ANYONE);
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [step, setStep] = useState<Step>(info.locations.length === 1 ? 'service' : 'location');
  const [history, setHistory] = useState<Step[]>([]);
  const [details, setDetails] = useState({ name: '', phone: '', email: '', smsOptIn: false, marketingOptIn: false, website: '' });
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<BookingResult | null>(null);
  // Turnstile tokens are single use: a refused booking renders a fresh check (resets).
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [resets, setResets] = useState(0);

  const location = info.locations.find((l) => l.name === locationName) ?? null;
  const service = location?.services.find((s) => s.name === serviceName) ?? null;
  const staffForService = useMemo(() => (location && service ? location.staff.filter((s) => s.services.length === 0 || s.services.includes(service.name)) : []), [location, service]);
  const days = useMemo(() => (location ? upcomingDays(location.timezone, location.hours) : []), [location]);

  const go = (next: Step) => {
    setHistory((h) => [...h, step]);
    setStep(next);
    setProblem(null);
    window.scrollTo?.(0, 0);
  };
  const back = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    setHistory(history.slice(0, -1));
    setStep(prev);
    setProblem(null);
  };

  const slots = useQuery({
    queryKey: ['booking-slots', slug, locationName, serviceName, staffName, date],
    enabled: step === 'time' && !!service && !!date,
    queryFn: () => {
      const q = new URLSearchParams({ service: serviceName!, date: date!, ...(locationName ? { location: locationName } : {}), ...(staffName ? { staff: staffName } : {}) });
      return get<{ availability: { slots: string[]; confirmation_required?: boolean } }>(`/api/public/book/${encodeURIComponent(slug)}/availability?${q}`);
    },
  });

  const book = useMutation({
    mutationFn: () =>
      post<{ booking: BookingResult }>(`/api/public/book/${encodeURIComponent(slug)}`, {
        name: details.name,
        phone: details.phone,
        email: details.email,
        smsOptIn: details.smsOptIn,
        marketingOptIn: details.marketingOptIn,
        website: details.website,
        location: locationName ?? undefined,
        service: serviceName,
        staff: staffName || undefined,
        date,
        time: slot!.slice(11, 16),
        ...(captcha ? { turnstileToken: captcha } : {}),
      }),
    onSuccess: (r) => {
      setResult(r.booking);
      setHistory([]);
      setStep('done');
    },
    onError: (e) => {
      setCaptcha(null);
      setResets((n) => n + 1);
      // Someone took the time meanwhile: back to the times, freshly loaded.
      if (e instanceof ApiError && e.data.reason === 'conflict') {
        setSlot(null);
        setStep('time');
        void slots.refetch();
      }
      setProblem(errorMessage(e));
    },
  });

  const header = (
    <div className="mb-6">
      {history.length && step !== 'done' ? (
        <Button variant="ghost" size="sm" className="-ml-3 mb-2" onClick={back}>
          <ArrowLeft /> Back
        </Button>
      ) : null}
      <p className="text-sm text-muted-foreground">Book online</p>
      <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.02em]">{info.business.name}</h1>
      {location && step !== 'location' ? (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="size-3.5" /> {location.name}
          {location.address ? ` · ${location.address}` : ''}
        </p>
      ) : null}
    </div>
  );

  const priceLine = (s: PublicService) => [`${s.durationMinutes} min`, s.priceCents != null ? formatMoney(s.priceCents) : null, s.depositCents ? `${formatMoney(s.depositCents)} deposit` : null].filter(Boolean).join(' · ');

  let content: ReactNode = null;
  if (step === 'location') {
    content = (
      <Section title="Choose a location">
        {info.locations.map((l) => (
          <Choice key={l.name} onClick={() => { setLocationName(l.name); setServiceName(null); go('service'); }}>
            <p className="font-medium">{l.name}</p>
            {l.address ? <p className="text-sm text-muted-foreground">{l.address}</p> : null}
          </Choice>
        ))}
      </Section>
    );
  } else if (step === 'service' && location) {
    content = (
      <Section title="Choose a service">
        {location.services.length === 0 ? <p className="text-sm text-muted-foreground">No services can be booked online here yet. Please call us.</p> : null}
        {location.services.map((s) => (
          <Choice
            key={s.name}
            onClick={() => {
              setServiceName(s.name);
              setStaffName(ANYONE);
              const qualified = location.staff.filter((p) => p.services.length === 0 || p.services.includes(s.name));
              go(qualified.length ? 'staff' : 'day');
            }}
          >
            <p className="font-medium">{s.name}</p>
            <p className="text-sm text-muted-foreground">{priceLine(s)}</p>
          </Choice>
        ))}
      </Section>
    );
  } else if (step === 'staff') {
    content = (
      <Section title="Who would you like?">
        <Choice onClick={() => { setStaffName(ANYONE); go('day'); }} selected={staffName === ANYONE}>
          <p className="font-medium">Anyone available</p>
          <p className="text-sm text-muted-foreground">The most open times</p>
        </Choice>
        {staffForService.map((p) => (
          <Choice key={p.name} onClick={() => { setStaffName(p.name); go('day'); }} selected={staffName === p.name}>
            <p className="font-medium">{p.name}</p>
          </Choice>
        ))}
      </Section>
    );
  } else if (step === 'day') {
    content = (
      <Section title="Pick a day">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {days.map((d) => (
            <Choice key={d.date} disabled={!d.open} selected={date === d.date} onClick={() => { setDate(d.date); setSlot(null); go('time'); }}>
              <p className="text-xs text-muted-foreground">{d.weekday}</p>
              <p className="font-medium">{d.label}</p>
              {!d.open ? <p className="text-xs text-muted-foreground">Closed</p> : null}
            </Choice>
          ))}
        </div>
      </Section>
    );
  } else if (step === 'time' && date) {
    const list = slots.data?.availability.slots ?? [];
    content = (
      <Section title={dayLabel(date)}>
        {problem ? <Alert variant="destructive">{problem}</Alert> : null}
        {slots.isLoading ? <div className="flex justify-center py-8"><Spinner /></div> : null}
        {slots.error ? <ErrorState title="Could not load times" error={slots.error} onRetry={() => slots.refetch()} /> : null}
        {slots.data && list.length === 0 ? <p className="text-sm text-muted-foreground">No open times this day. Try another day.</p> : null}
        <div className="grid grid-cols-3 gap-2">
          {list.map((s) => (
            <Button key={s} variant={slot === s ? 'primary' : 'outline'} className="h-11" onClick={() => { setSlot(s); go('details'); }}>
              {clockLabel(s)}
            </Button>
          ))}
        </div>
      </Section>
    );
  } else if (step === 'details' && service && slot) {
    const canSubmit = details.name.trim() && details.phone.trim() && (!info.turnstileSiteKey || captcha);
    content = (
      <Section title="Your details">
        <Card>
          <CardContent className="space-y-1 p-4 text-sm">
            <p className="font-medium">{service.name}{staffName ? ` with ${staffName}` : ''}</p>
            <p className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="size-3.5" /> {dayLabel(date!)} at {clockLabel(slot)} · {priceLine(service)}
            </p>
          </CardContent>
        </Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) book.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="bk-name">Name</Label>
            <Input id="bk-name" autoComplete="name" required value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-phone">Mobile phone</Label>
            <Input id="bk-phone" type="tel" inputMode="tel" autoComplete="tel" required value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-email">Email (optional)</Label>
            <Input id="bk-email" type="email" autoComplete="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} />
          </div>
          {/* Honeypot: off-screen and skipped by keyboard and screen readers; bots fill it in. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label htmlFor="bk-website">Website</label>
            <input id="bk-website" name="website" tabIndex={-1} autoComplete="off" value={details.website} onChange={(e) => setDetails({ ...details, website: e.target.value })} />
          </div>
          <Checkbox
            checked={details.smsOptIn}
            onCheckedChange={(v) => setDetails({ ...details, smsOptIn: v === true })}
            label={`Text me about this booking (confirmation and reminders from ${info.business.name}). Reply STOP any time.`}
          />
          {location?.offersRebooking ? (
            <Checkbox
              checked={details.marketingOptIn}
              onCheckedChange={(v) => setDetails({ ...details, marketingOptIn: v === true })}
              label={`Also text me when it's time to book again (occasional reminders from ${info.business.name}). Reply STOP any time.`}
            />
          ) : null}
          {info.turnstileSiteKey ? <TurnstileCheck key={resets} siteKey={info.turnstileSiteKey} onToken={setCaptcha} /> : null}
          {problem ? <Alert variant="destructive">{problem}</Alert> : null}
          <Button type="submit" size="lg" className="w-full" loading={book.isPending} disabled={!canSubmit}>
            {location?.requestMode ? 'Request this time' : 'Book'}
          </Button>
        </form>
      </Section>
    );
  } else if (step === 'done' && result) {
    content = <Confirmation result={result} businessName={info.business.name} />;
  }

  return (
    <>
      {header}
      {content}
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Confirmation({ result, businessName }: { result: BookingResult; businessName: string }) {
  const when = result.starts_at ? `${dayLabel(result.starts_at.slice(0, 10))} at ${clockLabel(result.starts_at)}` : null;
  // Deposits (when the business asks for one) come back as a payment link.
  const payUrl = [result.deposit_url, result.payment_url, result.checkout_url].find((u): u is string => typeof u === 'string' && /^https:\/\//.test(u));
  const depositCents = typeof result.deposit_cents === 'number' ? result.deposit_cents : null;
  const pending = result.needs_confirmation === true;
  const awaitingDeposit = result.status === 'pending_deposit' || !!payUrl;
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <CalendarCheck className="size-8 text-primary" aria-hidden />
        <h2 className="text-xl font-semibold">{awaitingDeposit ? 'Almost done: pay the deposit' : pending ? 'Request received' : "You're booked"}</h2>
        <p className="text-sm">
          {result.service}
          {result.staff ? ` with ${result.staff}` : ''}
          {when ? ` on ${when}` : ''}
          {result.location ? ` at ${result.location}` : ''}.
        </p>
        <p className="text-sm text-muted-foreground">
          {awaitingDeposit
            ? `Your time is held until the deposit${depositCents ? ` of ${formatMoney(depositCents)}` : ''} is paid.`
            : pending
              ? `${businessName} will confirm your time shortly.`
              : `See you then! ${businessName} has your booking.`}
        </p>
        {payUrl ? (
          <Button asChild size="lg" className="w-full">
            <a href={payUrl}>Pay deposit</a>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

type TurnstileApi = { render(el: HTMLElement, opts: Record<string, unknown>): string; remove(id: string): void };
let turnstileScript: Promise<TurnstileApi> | null = null;

/** Loads the Turnstile script once, only on pages that show the check. */
function loadTurnstile(): Promise<TurnstileApi> {
  turnstileScript ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => resolve((window as unknown as { turnstile: TurnstileApi }).turnstile);
    script.onerror = () => {
      turnstileScript = null;
      reject(new Error('Turnstile could not load'));
    };
    document.head.appendChild(script);
  });
  return turnstileScript;
}

/** The "are you a person" check before booking (shown when the business's API asks for it). */
function TurnstileCheck({ siteKey, onToken }: { siteKey: string; onToken: (token: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let id: string | undefined;
    let api: TurnstileApi | undefined;
    let gone = false;
    loadTurnstile()
      .then((ts) => {
        if (gone || !ref.current) return;
        api = ts;
        id = ts.render(ref.current, { sitekey: siteKey, callback: (t: string) => onToken(t), 'expired-callback': () => onToken(null), 'error-callback': () => onToken(null) });
      })
      .catch(() => onToken(null));
    return () => {
      gone = true;
      if (api && id) api.remove(id);
    };
  }, [siteKey, onToken]);
  return <div ref={ref} className="min-h-[65px]" />;
}
